import React from 'react';
import {
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '../../../components-ui/DropdownMenu';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '../../../components-ui/Command';
import { NameStyled } from './PerformanceChartHeader/PerformanceChartHeader.styles';
import { SlidersHorizontal } from 'lucide-react';
import AvatarProvider from '../../../components/AvatarProvider';
import NameTranslation from '../../../components/NameTranslation';
import Checkbox from '../../../components/Checkbox';
import Typography from '../../../components/Typography';
import { AdAccountFilterItem, useContentFilterStore } from '../../../store/content-filter';
import { useProviders } from '../../../context/ProvidersProvider';
import {
  NexoyaContentFilterFieldName,
  NexoyaContentFilterOperator,
  NexoyaParentContentProviderFilter,
  NexoyaProvider,
} from '../../../types';
import { useProviderSubAccountsQuery } from '../../../graphql/portfolioRules/queryProviderSubAccounts';
import { CONTENT_TYPE_SUB_ACCOUNT_NUMBER } from '../utils/portfolio-rules';
import translate from '../../../utils/translate';
import useTranslationStore from '../../../store/translations';

type Props = {
  portfolioId: number;
  availableProviders: NexoyaParentContentProviderFilter[];
};

export const ChannelsFilterSection = ({ portfolioId, availableProviders }: Props) => {
  const { translations } = useTranslationStore();
  const { providerById } = useProviders();
  const {
    adAccountsFilter,
    providersFilter,
    handleAddAdAccount,
    handleRemoveAdAccount,
    handleSelectChannel,
    handleDeselectChannel,
    handleAddProvider,
    handleRemoveProvider,
  } = useContentFilterStore();

  const { data: subAccountsData, loading: subAccountsLoading } = useProviderSubAccountsQuery({
    portfolioId,
    filters: [
      {
        fieldName: NexoyaContentFilterFieldName.ContentType,
        operator: NexoyaContentFilterOperator.Eq,
        value: { number: CONTENT_TYPE_SUB_ACCOUNT_NUMBER },
      },
    ],
    excludePortfolioContents: false,
    skip: !portfolioId,
  });

  const subAccounts = React.useMemo(
    () =>
      (subAccountsData?.filterContents || [])
        .filter((c) => c.sourceProvider?.provider_id != null)
        .map((c) => ({
          contentId: c.contentId,
          title: c.title,
          providerId: c.sourceProvider.provider_id,
        })),
    [subAccountsData],
  );

  const getChannelState = (providerId: number) => {
    const providerAccounts = subAccounts.filter((a) => a.providerId === providerId);
    const selectedCount = adAccountsFilter.filter((a) => a.providerId === providerId).length;
    return {
      checked: providerAccounts.length > 0 && selectedCount === providerAccounts.length,
      indeterminate: selectedCount > 0 && selectedCount < providerAccounts.length,
      providerAccounts,
    };
  };

  const handleToggleChannel = (providerId: number, fullProvider: NexoyaProvider) => {
    const { checked, providerAccounts } = getChannelState(providerId);

    if (providerAccounts.length === 0) {
      // Provider has no ad accounts — fall back to providersFilter only
      const isProviderActive = providersFilter.some((p) => p.provider_id === providerId);
      if (isProviderActive) {
        handleRemoveProvider(fullProvider);
      } else {
        handleAddProvider(fullProvider);
      }
      return;
    }

    if (checked) {
      handleDeselectChannel(fullProvider);
    } else {
      const allAccounts: AdAccountFilterItem[] = providerAccounts.map((a) => ({
        contentId: a.contentId,
        title: a.title,
        providerId: a.providerId,
      }));
      handleSelectChannel(fullProvider, allAccounts);
    }
  };

  const totalItems = subAccounts.length + availableProviders.length;

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger disabled={subAccountsLoading || !availableProviders?.length}>
        <SlidersHorizontal className="mr-2 h-4 w-4" />
        <span>Channels</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className="max-h-96 overflow-y-auto p-0" onKeyDown={(e) => e.stopPropagation()}>
          <Command>
            {totalItems > 5 && (
              <CommandInput placeholder="Search channels & accounts..." autoFocus className="h-9" />
            )}
            <CommandList>
              {availableProviders?.map((provider) => {
                const fullProvider = providerById(provider.providerId) as NexoyaProvider;
                const { checked, indeterminate, providerAccounts } = getChannelState(provider.providerId);

                // For providers with no ad accounts, use providersFilter
                const isProviderOnlyActive =
                  providerAccounts.length === 0 && providersFilter.some((p) => p.provider_id === provider.providerId);

                const channelChecked = providerAccounts.length > 0 ? checked : isProviderOnlyActive;
                const channelIndeterminate = providerAccounts.length > 0 ? indeterminate : false;

                return (
                  <CommandGroup key={provider.providerId}>
                    <CommandItem
                      value={translate(translations, fullProvider?.name)}
                      onSelect={() => handleToggleChannel(provider.providerId, fullProvider)}
                      className="flex w-full cursor-pointer justify-between font-medium"
                    >
                      <span className="flex items-center gap-1.5">
                        <AvatarProvider providerId={provider.providerId} size={15} color="dark" />
                        <NameStyled>
                          <NameTranslation text={fullProvider?.name} />
                        </NameStyled>
                      </span>
                      <Checkbox
                        style={{ padding: 4 }}
                        checked={channelChecked || channelIndeterminate}
                        indeterminate={channelIndeterminate}
                        color="dark"
                      />
                    </CommandItem>

                    {providerAccounts.map((account) => {
                      const isActive = adAccountsFilter.some((f) => f.contentId === account.contentId);
                      const adAccount: AdAccountFilterItem = {
                        contentId: account.contentId,
                        title: account.title,
                        providerId: account.providerId,
                      };
                      return (
                        <CommandItem
                          key={account.contentId}
                          value={translate(translations, account.title)}
                          onSelect={() => {
                            if (isActive) {
                              handleRemoveAdAccount(adAccount);
                              // Break "whole channel" grouping if provider was fully selected
                              if (providersFilter.some((p) => p.provider_id === account.providerId)) {
                                handleRemoveProvider(fullProvider);
                              }
                            } else {
                              handleAddAdAccount(adAccount);
                              // Auto-promote to "Channel" chip if this completes the full set
                              const currentSelectedCount = adAccountsFilter.filter(
                                (a) => a.providerId === account.providerId,
                              ).length;
                              if (currentSelectedCount + 1 === providerAccounts.length) {
                                handleAddProvider(fullProvider);
                              }
                            }
                          }}
                          className="flex w-full cursor-pointer justify-between pl-7"
                        >
                          <NameStyled>
                            <Typography>{translate(translations, account.title)}</Typography>
                          </NameStyled>
                          <Checkbox style={{ padding: 4 }} checked={isActive} color="dark" />
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                );
              })}
            </CommandList>
          </Command>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
};
